import { ChangeEvent, FC, ReactElement, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  importProductCapabilitiesAction,
  resetImportStateAction,
} from "../redux/ProductCapabilitiesActions";
import { validateImportFile } from "../utils/validateFile";
import { ProductCapabilitiesImportErrorTable } from "./ProductCapabilitiesImportErrorTable";

interface Props {
  plantCode: string;
}

export const ProductCapabilitiesImportSection: FC<Props> = ({ plantCode }): ReactElement => {
  const dispatch = useDispatch();
  const [file, setFile] = useState<File | null>(null);
  const [clientError, setClientError] = useState<string | null>(null);

  const { importing, importResult, importErrors } = useSelector(
    (state: any) => ({
      importing: state.productCapabilities.importing,
      importResult: state.productCapabilities.importResult,
      importErrors: state.productCapabilities.importErrors,
    }),
    shallowEqual
  );

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    // Picking a new file invalidates the previous run's success/error state.
    dispatch(resetImportStateAction());

    const selected = e.target.files && e.target.files.length > 0 ? e.target.files[0] : null;
    setFile(selected);
    if (!selected) {
      setClientError(null);
      return;
    }
    const result = validateImportFile(selected);
    setClientError(result.ok ? null : result.reason ?? "Invalid file");
  };

  const handleSubmit = () => {
    const result = validateImportFile(file);
    if (!result.ok) {
      setClientError(result.reason ?? "Invalid file");
      return;
    }
    setClientError(null);
    dispatch(importProductCapabilitiesAction(plantCode, file as File) as any);
  };

  return (
    <div data-testid="product-capabilities-import-section">
      <h4>Import</h4>
      <Form.Group controlId="product-capabilities-import-file">
        <Form.Label>CSV file (max 1 MB)</Form.Label>
        <Form.Control
          type="file"
          accept=".csv,text/csv"
          onChange={handleFileChange}
          data-testid="product-capabilities-import-file-input"
        />
      </Form.Group>
      {clientError && (
        <div className="text-danger mt-2" data-testid="product-capabilities-client-error">
          {clientError}
        </div>
      )}
      <Button
        variant="primary"
        onClick={handleSubmit}
        disabled={importing || !plantCode || !file || !!clientError}
        data-testid="product-capabilities-import-button"
        className="mt-2"
      >
        {importing ? "Uploading…" : "Upload CSV"}
      </Button>

      {importResult && (
        <div className="text-success mt-3" data-testid="product-capabilities-import-success">
          Imported {importResult.imported} capabilities.
        </div>
      )}

      <div className="mt-3">
        <ProductCapabilitiesImportErrorTable errors={importErrors} />
      </div>
    </div>
  );
};
